import React from 'react'

const Jva_stk = () => {
    return (
        <div className='container'>
            <td>
    
<h1 class="h1">Java Stack</h1>
<p>The <strong>stack</strong> is a linear data structure that is used to store the collection of objects. It is
      based on <strong>Last-In-First-Out</strong> (LIFO). Java collection framework provides many
      interfaces and classes to store the collection of objects. One of them is the <strong>Stack class</strong>
      that provides different operations such as push, pop, search, etc.</p>
<p>In this section, we will discuss the <strong>Java Stack class</strong>, its <strong>methods,</strong> and
      <strong>implement</strong> the stack data structure in a Java program. But before moving to the Java Stack
      class have a quick view of how the stack works.</p>
<p>The stack data structure has the two most important operations that are <strong>push</strong> and
      <strong>pop</strong>. The push operation inserts an element into the stack and pop operation removes an
      element from the top of the stack. Let's see how they work on stack.</p>
<img src="https://static.javatpoint.com/core/images/java-stack.png" alt="Java Stack"/>
<p>Let's push 20, 13, 89, 90, 11, 45, 18, respectively into the stack.</p>
<img src="https://static.javatpoint.com/core/images/java-stack2.png" alt="Java Stack"/>
<p>Let's remove (pop) 18, 45, and 11 from the stack.</p>
<img src="https://static.javatpoint.com/core/images/java-stack3.png" alt="Java Stack"/>
<p><strong>Empty Stack:</strong> If the stack has no element is known as an <strong>empty stack</strong>. When the
      stack is empty the value of the top variable is -1.</p>
<img src="https://static.javatpoint.com/core/images/java-stack4.png" alt="Java Stack"/>
<p>When we push an element into the stack the top is <strong>increased by 1</strong>. In the following figure,</p>
<ul class="points">
      <li>Push 12, top=0</li>
      <li>Push 6, top=1</li>
      <li>Push 9, top=2</li>
</ul>
<img src="https://static.javatpoint.com/core/images/java-stack5.png" alt="Java Stack"/>
<p>When we pop an element from the stack the value of top is <strong>decreased by 1</strong>. In the following
      figure, we have popped 9.</p>
<img src="https://static.javatpoint.com/core/images/java-stack6.png" alt="Java Stack"/>
<p>The following table shows the different values of the top.</p>
<img src="https://static.javatpoint.com/core/images/java-stack7.png" alt="Java Stack"/>
<h2 class="h2">Java Stack Class</h2>
<p>In Java, <strong>Stack</strong> is a class that falls under the Collection framework that extends the
      <strong>Vector</strong> class. It also implements interfaces <strong>List, Collection, Iterable, Cloneable,
            Serializable.</strong> It represents the LIFO stack of objects. Before using the Stack class, we must
      import the <span class="hlight">java.util</span> package. The stack class arranged in the Collections
      framework hierarchy, as shown below.</p>
<img src="https://static.javatpoint.com/core/images/java-stack8.png" alt="Java Stack"/>
<h2 class="h2">Stack Class Constructor</h2>
<p>The Stack class contains only the <strong>default constructor</strong> that creates an empty stack.</p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">public</span><span>&nbsp;Stack()&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<h2 class="h2">Creating a Stack</h2>
<p>If we want to create a stack, first, import the <span class="hlight">java.util</span> package and create an
      object of the Stack class.</p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span>Stack&nbsp;stk&nbsp;=&nbsp;</span><span
                                    class="keyword">new</span><span>&nbsp;Stack();&nbsp;&nbsp;</span></span></li>
            </ul>
      </div>
</div>
<p>Or</p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span>Stack&lt;type&gt;&nbsp;stk&nbsp;=&nbsp;</span><span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<p>Where type denotes the type of stack like Integer, String, etc.</p>
<h2 class="h2">Methods of the Stack Class</h2>
<p>We can perform push, pop, peek and search operation on the stack. The Java Stack class provides mainly five
      methods to perform these operations. Along with this, it also provides all the methods of the <a
            href="https://www.javatpoint.com/java-vector">Java Vector class</a>.</p>
<table class="alt">
      <tbody>
            <tr>
                  <th>Method</th>
                  <th>Modifier and Type</th>
                  <th>Method Description</th>
            </tr>
            <tr>
                  <td><a href="#empty">empty()</a></td>
                  <td>boolean</td>
                  <td>The method checks the stack is empty or not.</td>
            </tr>
            <tr>
                  <td><a href="#push">push(E item)</a></td>
                  <td>E</td>
                  <td>The method pushes (insert) an element onto the top of the stack.</td>
            </tr>
            <tr>
                  <td><a href="#pop">pop()</a></td>
                  <td>E</td>
                  <td>The method removes an element from the top of the stack and returns the same element as the
                        value of that function.</td>
            </tr>
            <tr>
                  <td><a href="#peek">peek()</a></td>
                  <td>E</td>
                  <td>The method looks at the top element of the stack without removing it.</td>
            </tr>
            <tr>
                  <td><a href="#search">search(Object o)</a></td>
                  <td>int</td>
                  <td>The method searches the specified object and returns the position of the object.</td>
            </tr>
      </tbody>
</table>
<h3 id="empty" class="h3">Stack Class empty() Method</h3>
<p>The <strong>empty()</strong> method of the Stack class check the stack is empty or not. If the stack is empty,
      it returns true, else returns false. We can also use the isEmpty() method of the Vector class.
</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">boolean</span><span>&nbsp;empty()&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<p><strong>Returns:</strong> The method returns true if the stack is empty, else returns false.</p>
<p>In the following example, we have created an instance of the Stack class. After that, we have invoked the
      empty() method two times. The first time it returns <strong>true</strong> because we have not pushed any
      element into the stack. After that, we have pushed elements into the stack. Again we have invoked the
      empty() method that returns <strong>false</strong> because the stack is not empty.</p>
<p><strong>StackEmptyMethodExample.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Stack;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackEmptyMethodExample&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span
                                    class="comment">//creating&nbsp;an&nbsp;instance&nbsp;of&nbsp;Stack&nbsp;class</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>Stack&lt;Integer&gt;&nbsp;stk=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="comment">//&nbsp;checking&nbsp;stack&nbsp;is&nbsp;empty&nbsp;or&nbsp;not</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="keyword">boolean</span><span>&nbsp;result&nbsp;=&nbsp;stk.empty();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Is&nbsp;the&nbsp;stack&nbsp;empty?&nbsp;"</span><span>&nbsp;+&nbsp;result);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//&nbsp;pushing&nbsp;elements&nbsp;into&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">78</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">113</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">90</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">120</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="comment">//prints&nbsp;elements&nbsp;of&nbsp;the&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"Elements&nbsp;in&nbsp;Stack:&nbsp;"</span><span>&nbsp;+&nbsp;stk);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>result&nbsp;=&nbsp;stk.empty();&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"Is&nbsp;the&nbsp;stack&nbsp;empty?&nbsp;"</span><span>&nbsp;+&nbsp;result);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>Is the stack empty? true <br/>
Elements in Stack: [78, 113, 90, 120] <br/>
Is the stack empty? false <br/>
</pre>
</div>
<h3 id="push" class="h3">Stack Class push() Method</h3>
<p>The method inserts an item onto the top of the stack. It works the same as the method addElement(item) method of the
      Vector class. It passes a parameter <strong>item</strong> to be pushed into the stack.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">public</span><span>&nbsp;E&nbsp;push(E&nbsp;item)&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<p><strong>Parameter:</strong> An item to be pushed onto the top of the stack.</p>
<p><strong>Returns:</strong> The method returns the argument that we have passed as a parameter.</p>
<h3 id="pop" class="h3">Stack Class pop() Method</h3>
<p>The method removes an object at the top of the stack and returns the same object. It throws
      <strong>EmptyStackException</strong> if the stack is empty.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">public</span><span>&nbsp;E&nbsp;pop()&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<p><strong>Returns:</strong> It returns an object that is at the top of the stack.</p>
<p>Let's implement the stack in a Java program and perform push and pop operations.</p>
<p><strong>StackPushPopExample.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.*;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackPushPopExample&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main(String&nbsp;args[])&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span
                                    class="comment">//creating&nbsp;an&nbsp;object&nbsp;of&nbsp;Stack&nbsp;class</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>Stack&nbsp;&lt;Integer&gt;&nbsp;stk&nbsp;=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"stack:&nbsp;"</span><span>&nbsp;+&nbsp;stk);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//pushing&nbsp;elements&nbsp;into&nbsp;the&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>pushelmnt(stk,&nbsp;<span
                                    class="number">20</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>pushelmnt(stk,&nbsp;<span
                                    class="number">13</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>pushelmnt(stk,&nbsp;<span
                                    class="number">89</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>pushelmnt(stk,&nbsp;<span
                                    class="number">90</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>pushelmnt(stk,&nbsp;<span
                                    class="number">11</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>pushelmnt(stk,&nbsp;<span
                                    class="number">45</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>pushelmnt(stk,&nbsp;<span
                                    class="number">18</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span><span
                                    class="comment">//popping&nbsp;elements&nbsp;from&nbsp;the&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>popelmnt(stk);&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>popelmnt(stk);&nbsp;&nbsp;</span></li>
                  <li class=""><span><span
                                    class="comment">//throws&nbsp;exception&nbsp;if&nbsp;the&nbsp;stack&nbsp;is&nbsp;empty</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span class="keyword">try</span><span>&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>popelmnt(stk);&nbsp;&nbsp;</span></li>
                  <li class=""><span>&#125;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span
                                    class="keyword">catch</span><span>&nbsp;(EmptyStackException&nbsp;e)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"empty&nbsp;stack"</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span
                                    class="comment">//performing&nbsp;push&nbsp;operation</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;pushelmnt(Stack&nbsp;stk,&nbsp;</span><span
                                    class="keyword">int</span><span>&nbsp;x)&nbsp;&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span
                                    class="comment">//invoking&nbsp;push()&nbsp;method&nbsp;&nbsp;&nbsp;&nbsp;</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span
                                    class="keyword">new</span><span>&nbsp;Integer(x));&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"push&nbsp;-&gt;&nbsp;"</span><span>&nbsp;+&nbsp;x);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="comment">//prints&nbsp;modified&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"stack:&nbsp;"</span><span>&nbsp;+&nbsp;stk);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span
                                    class="comment">//performing&nbsp;pop&nbsp;operation</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;popelmnt(Stack&nbsp;stk)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span>System.out.print(<span
                                    class="string">"pop&nbsp;-&gt;&nbsp;"</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//invoking&nbsp;pop()&nbsp;method&nbsp;</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>Integer&nbsp;x&nbsp;=&nbsp;(Integer)&nbsp;stk.pop();&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>System.out.println(x);&nbsp;&nbsp;</span></li>
                  <li class=""><span><span
                                    class="comment">//prints&nbsp;modified&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"stack:&nbsp;"</span><span>&nbsp;+&nbsp;stk);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>stack: [] <br/>
push -&gt; 20 <br/>
stack: [20] <br/>
push -&gt; 13 <br/>
stack: [20, 13] <br/>
push -&gt; 89 <br/>
stack: [20, 13, 89] <br/>
push -&gt; 90 <br/>
stack: [20, 13, 89, 90] <br/>
push -&gt; 11 <br/>
stack: [20, 13, 89, 90, 11] <br/>
push -&gt; 45 <br/>
stack: [20, 13, 89, 90, 11, 45] <br/>
push -&gt; 18 <br/>
stack: [20, 13, 89, 90, 11, 45, 18] <br/>
pop -&gt; 18 <br/>
stack: [20, 13, 89, 90, 11, 45] <br/>
pop -&gt; 45 <br/>
stack: [20, 13, 89, 90, 11] <br/>
pop -&gt; 11 <br/>
stack: [20, 13, 89, 90] <br/>
</pre> <br/>
</div>
<h3 id="peek" class="h3">Stack Class peek() Method</h3>
<p>It looks at the element that is at the top in the stack. It also throws <strong>EmptyStackException</strong> if
      the stack is empty.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">public</span><span>&nbsp;E&nbsp;peek()&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div><textarea name="code" class="java" >public E peek()
</textarea>
</div>
<p><strong>Returns:</strong> It returns the top elements of the stack.</p>
<p>Let's see an example of the peek() method.</p>
<p><strong>StackPeekMethodExample.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Stack;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackPeekMethodExample&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span>Stack&lt;String&gt;&nbsp;stk=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//&nbsp;pushing&nbsp;elements&nbsp;into&nbsp;Stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span
                                    class="string">"Apple"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>stk.push(<span
                                    class="string">"Grapes"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>stk.push(<span
                                    class="string">"Mango"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>stk.push(<span
                                    class="string">"Orange"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Stack:&nbsp;"</span><span>&nbsp;+&nbsp;stk);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//&nbsp;Access&nbsp;element&nbsp;from&nbsp;the&nbsp;top&nbsp;of&nbsp;the&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>String&nbsp;fruits&nbsp;=&nbsp;stk.peek();&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span
                                    class="comment">//prints&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Element&nbsp;at&nbsp;top:&nbsp;"</span><span>&nbsp;+&nbsp;fruits);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>Stack: [Apple, Grapes, Mango, Orange]
Element at the top of the stack: Orange
</pre>
</div>
<h3 id="search" class="h3">Stack Class search() Method</h3>
<p>The method searches the object in the stack from the top. It parses a parameter that we want to search for. It
      returns the 1-based location of the object in the stack. Thes topmost object of the stack is considered at
      distance 1.</p>
<p>Suppose, o is an object in the stack that we want to search for. The method returns the distance from the top
      of the stack of the occurrence nearest the top of the stack. It uses <strong>equals()</strong> method to
      search an object in the stack.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">int</span><span>&nbsp;search(Object&nbsp;o)&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<p><strong>Parameter:</strong> o is the desired object to be searched.</p>
<p><strong>Returns:</strong> It returns the object location from the top of the stack. If it returns -1, it means
      that the object is not on the stack.</p>
<p>Let's see an example of the search() method.</p>
<p><strong>StackSearchMethodExample.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Stack;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackSearchMethodExample&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span>Stack&lt;String&gt;&nbsp;stk=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//pushing&nbsp;elements&nbsp;into&nbsp;Stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span
                                    class="string">"Mac&nbsp;Book"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>stk.push(<span
                                    class="string">"HP"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>stk.push(<span class="string">"DELL"</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span
                                    class="string">"Asus"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Stack:&nbsp;"</span><span>&nbsp;+&nbsp;stk);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//&nbsp;Search&nbsp;an&nbsp;element</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="keyword">int</span><span>&nbsp;location&nbsp;=&nbsp;stk.search(</span><span
                                    class="string">"HP"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"Location&nbsp;of&nbsp;Dell:&nbsp;"</span><span>&nbsp;+&nbsp;location);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<h2 class="h2">Java Stack Operations</h2>
<h3 class="h3">Size of the Stack</h3>
<p>We can also find the size of the stack using the size() method of the Vector class. It
      returns the total number of elements (size of the stack) in the stack.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">int</span><span>&nbsp;size()&nbsp;&nbsp;</span></span></li>
            </ul>
      </div><textarea name="code" class="java" >public int size()
</textarea>
</div>
<p>Let's see an example of the size() method of the Vector class.</p>
<p><strong>StackSizeExample.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Stack;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackSizeExample&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main&nbsp;(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class=""><span>Stack&nbsp;stk&nbsp;=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack();&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>stk.push(<span class="number">22</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">33</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">44</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">55</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">66</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="comment">//&nbsp;Checks&nbsp;the&nbsp;Stack&nbsp;is&nbsp;empty&nbsp;or&nbsp;not</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="keyword">boolean</span><span>&nbsp;rslt=stk.empty();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Is&nbsp;the&nbsp;stack&nbsp;empty&nbsp;or&nbsp;not?&nbsp;"</span><span>&nbsp;+rslt);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//&nbsp;Find&nbsp;the&nbsp;size&nbsp;of&nbsp;the&nbsp;Stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="keyword">int</span><span>&nbsp;x=stk.size();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>System.out.println(<span
                                    class="string">"The&nbsp;stack&nbsp;size&nbsp;is:&nbsp;"</span><span>+x);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>Is the stack empty or not? false
The stack size is: 5
</pre>
</div>
<h3 class="h3">Iterate Elements</h3>
<p>Iterate means to fetch the elements of the stack. We can fetch elements of the stack using three different
      methods are as follows:</p>
<ul class="points">
      <li>Using <strong>iterator()</strong> Method</li>
      <li>Using <strong>forEach()</strong> Method</li>
      <li>Using <strong>listIterator()</strong> Method</li>
</ul>
<h3 class="h4">Using the iterator() Method</h3>
<p>It is the method of the Iterator interface. It returns an iterator over the elements in the stack. Before using
      the iterator() method import the <span class="hlight">java.util.Iterator</span> package.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span>Iterator&lt;T&gt;&nbsp;iterator()&nbsp;&nbsp;</span></span></li>
            </ul>
      </div>
</div>
<p>Let's perform an iteration over the stack.</p>
<p><strong>StackIterationExample1.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Iterator;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Stack;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackIterationExample1&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main&nbsp;(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span
                                    class="comment">//creating&nbsp;an&nbsp;object&nbsp;of&nbsp;Stack&nbsp;class</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>Stack&nbsp;stk&nbsp;=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack();&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span><span
                                    class="comment">//pushing&nbsp;elements&nbsp;into&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="string">"BMW"</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span
                                    class="string">"Audi"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>stk.push(<span
                                    class="string">"Ferrari"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span>stk.push(<span
                                    class="string">"Bugatti"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class=""><span>stk.push(<span
                                    class="string">"Jaguar"</span><span>);&nbsp;&nbsp;</span></span></li>
                  <li class="alt"><span><span
                                    class="comment">//iteration&nbsp;over&nbsp;the&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>Iterator&nbsp;iterator&nbsp;=&nbsp;stk.iterator();&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span
                                    class="keyword">while</span><span>(iterator.hasNext())&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>Object&nbsp;values&nbsp;=&nbsp;iterator.next();&nbsp;&nbsp;</span></li>
                  <li class=""><span>System.out.println(values);&nbsp;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>BMW <br/>
Audi <br/>
Ferrari <br/>
Bugatti <br/>
Jaguar <br/>
</pre>
</div>
<h3 class="h4">Using the forEach() Method</h3>
<p>Java provides a forEach() method to iterate over the elements. The method is defined in the
      <strong>Iterable</strong> and <strong>Stream</strong> interface.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span class="keyword">default</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;forEach(Consumer&lt;</span><span
                                    class="keyword">super</span><span>&nbsp;T&gt;action)&nbsp;&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
            </ul>
      </div>
</div>
<p>Let's iterate over the stack using the forEach() method.</p>
<p><strong>StackIterationExample2.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.*;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackIterationExample2&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main&nbsp;(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class=""><span><span
                                    class="comment">//creating&nbsp;an&nbsp;instance&nbsp;of&nbsp;Stack&nbsp;class</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>Stack&nbsp;&lt;Integer&gt;&nbsp;stk&nbsp;=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="comment">//pushing&nbsp;elements&nbsp;into&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">119</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">203</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">988</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Iteration&nbsp;over&nbsp;the&nbsp;stack&nbsp;using&nbsp;forEach()&nbsp;Method:"</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="comment">//invoking&nbsp;forEach()&nbsp;method&nbsp;for&nbsp;iteration&nbsp;over&nbsp;the&nbsp;stack</span><span>&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.forEach(n&nbsp;-&gt;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class=""><span>System.out.println(n);&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;);&nbsp;&nbsp;</span></li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>Iteration over the stack using forEach() Method: <br/>
119 <br/>
203 <br/>
988 <br/>
</pre>
</div>
<h3 class="h4">Using listIterator() Method</h3>
<p>This method returns a list iterator over the elements in the mentioned list (in sequence), starting at the
      specified position in the list. It iterates the stack from top to bottom.</p>
<p><strong>Syntax</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span>ListIterator&nbsp;listIterator(</span><span
                                    class="keyword">int</span><span>&nbsp;index)&nbsp;&nbsp;</span></span></li>
            </ul>
      </div><textarea name="code" class="java" >ListIterator listIterator(int index)
</textarea>
</div>
<p><strong>Parameter:</strong> The method parses a parameter named <strong>index.</strong> </p>
<p><strong>Returns:</strong> This method returns a list iterator over the elements, in sequence.</p>
<p><strong>Exception:</strong> It throws <strong>IndexOutOfBoundsException</strong> if the index is out of range.
</p>
<p>Let's iterate over the stack using the listIterator() method.</p>
<p><strong>StackIterationExample3.java</strong></p>
<div class="containers">
      <div class="dp-highlighter">
            
                 
            <ul start="1" class="dp-j">
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Iterator;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.ListIterator;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="keyword">import</span><span>&nbsp;java.util.Stack;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&nbsp;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">class</span><span>&nbsp;StackIterationExample3&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span><span class="keyword">public</span><span>&nbsp;</span><span
                                    class="keyword">static</span><span>&nbsp;</span><span
                                    class="keyword">void</span><span>&nbsp;main&nbsp;(String[]&nbsp;args)&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>Stack&nbsp;&lt;Integer&gt;&nbsp;stk&nbsp;=&nbsp;<span
                                    class="keyword">new</span><span>&nbsp;Stack&lt;&gt;();&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">119</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span>stk.push(<span class="number">203</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>stk.push(<span class="number">988</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt">
                        <span>ListIterator&lt;Integer&gt;&nbsp;ListIterator&nbsp;=&nbsp;stk.listIterator(stk.size());&nbsp;&nbsp;</span>
                  </li>
                  <li class=""><span>System.out.println(<span
                                    class="string">"Iteration&nbsp;over&nbsp;the&nbsp;Stack&nbsp;from&nbsp;top&nbsp;to&nbsp;bottom:"</span><span>);&nbsp;&nbsp;</span></span>
                  </li>
                  <li class="alt"><span><span
                                    class="keyword">while</span><span>&nbsp;(ListIterator.hasPrevious())&nbsp;&nbsp;&nbsp;</span></span>
                  </li>
                  <li class=""><span>&#123;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>Integer&nbsp;avg&nbsp;=&nbsp;ListIterator.previous();&nbsp;&nbsp;</span>
                  </li>
                  <li class=""><span>System.out.println(avg);&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class=""><span>&#125;&nbsp;&nbsp;</span></li>
                  <li class="alt"><span>&#125;&nbsp;&nbsp;</span></li>
            </ul>
      </div>
</div>
<p><strong>Output:</strong></p>
<div class="output">
      <pre>Iteration over the Stack from top to bottom: <br/>
988 <br/>
203 <br/>
119 <br/>
</pre>
</div>

<hr/>

</td>
        </div>
    )
}

export default Jva_stk
